import { CSSProperties, ReactNode } from 'react';

/**
 * Shared RepVantage brand primitives for every pre-auth / splash surface
 * (sign-in, magic-redeem, access-denied, code-redeem, request-access, copy-code,
 * PIN login/setup, loaders). Single source of truth so all the doors into PREP
 * look uniform and on-brand.
 *
 * Brand canon (repvantage-brand-identity v3):
 *   · Background  · Deep Field #0A1A10 (green-black, never pure black)
 *   · Accent      · Emerald #34d399 (primary), Amber #F59E0B (VANTAGE / YC)
 *   · Text        · Slate scale #E2E8F0 / #94A3B8 / #64748B / #475569
 *   · Type        · Orbitron (wordmark) · Jost (body) · Share Tech Mono (status)
 *   · Glass       · green-tinted rgba(52,211,153,a), never white-tinted
 *
 * Visual-only. Nothing here touches auth handlers, state, or navigation.
 */

export const BRAND = {
  bg: '#0A1A10',
  text: '#E2E8F0',
  textSec: '#94A3B8',
  textMuted: '#64748B',
  textDim: '#475569',
  accent: '#34d399',
  accentInk: '#06281c',
  amber: '#F59E0B',
  red: '#EF4444',
  redText: '#FCA5A5',
  surface: 'rgba(52,211,153,0.05)',
  surfaceStrong: 'rgba(52,211,153,0.08)',
  border: 'rgba(52,211,153,0.18)',
  borderStrong: 'rgba(52,211,153,0.34)',
  fontBody: "'Jost', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
  fontDisplay: "'Orbitron', sans-serif",
  fontMono: "'Share Tech Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  ease: 'cubic-bezier(0.16, 1, 0.3, 1)',
  // Matches the live marketing site (repvantage.io) treatment exactly: a GREEN
  // linear-gradient base (not flat near-black) + a strong 0.20 emerald radial +
  // a faint amber spot. The flat-#0A1A10 base was what read as black.
  screenBg:
    'radial-gradient(58% 55% at 50% 32%, rgba(52,211,153,0.20) 0%, rgba(52,211,153,0.06) 46%, rgba(13,27,17,0) 72%), radial-gradient(70% 50% at 84% 8%, rgba(245,158,11,0.06) 0%, rgba(13,27,17,0) 60%), linear-gradient(160deg, #0C1E15 0%, #091210 55%, #0D1B11 100%)',
} as const;

/** Full-screen container for a standalone splash surface. */
export const brandScreen: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 20px',
  background: BRAND.screenBg,
  color: BRAND.text,
  fontFamily: BRAND.fontBody,
  boxSizing: 'border-box',
  textAlign: 'center',
};

/** Solid emerald primary CTA. Pair with className="wedge-cta-emerald" for hover. */
export const emeraldBtn = (busy = false): CSSProperties => ({
  width: '100%',
  padding: 14,
  borderRadius: 14,
  border: 0,
  background: busy ? 'rgba(52,211,153,0.42)' : BRAND.accent,
  color: BRAND.accentInk,
  fontSize: 14.5,
  fontWeight: 700,
  cursor: busy ? 'default' : 'pointer',
  fontFamily: 'inherit',
  transition: `all 200ms ${BRAND.ease}`,
  boxShadow: busy ? 'none' : '0 6px 18px rgba(52,211,153,0.28)',
});

/** Green-tinted glass secondary / ghost button. */
export const glassBtn: CSSProperties = {
  width: '100%',
  padding: 13,
  borderRadius: 14,
  border: `1px solid ${BRAND.border}`,
  background: BRAND.surface,
  color: BRAND.text,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: `all 200ms ${BRAND.ease}`,
};

/** Quiet text link / back button. */
export const linkBtn: CSSProperties = {
  width: '100%',
  marginTop: 10,
  background: 'none',
  border: 0,
  color: BRAND.textSec,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  padding: '8px',
};

/** Text input - green glass, emerald focus. */
export const brandField = (focused: boolean, extra: CSSProperties = {}): CSSProperties => ({
  width: '100%',
  boxSizing: 'border-box',
  marginBottom: 12,
  padding: '13px 15px',
  borderRadius: 12,
  border: `1px solid ${focused ? 'rgba(52,211,153,0.55)' : BRAND.border}`,
  background: focused ? 'rgba(52,211,153,0.08)' : BRAND.surface,
  color: BRAND.text,
  fontSize: 15,
  fontFamily: 'inherit',
  outline: 'none',
  transition: `all 200ms ${BRAND.ease}`,
  boxShadow: focused ? '0 0 0 4px rgba(52,211,153,0.12)' : 'none',
  ...extra,
});

/** Inline error box. */
export const errorBox: CSSProperties = {
  color: BRAND.redText,
  fontSize: 12.5,
  margin: '2px 0 12px',
  lineHeight: 1.4,
  padding: '8px 12px',
  background: 'rgba(239,68,68,0.08)',
  border: '1px solid rgba(239,68,68,0.22)',
  borderRadius: 10,
};

/**
 * Keyframes + hover/focus rules shared by every standalone surface. Drop one
 * <BrandStyleTag /> per full-screen surface (the embedded forms inherit the
 * rules from their host shell's style tag, which carries the same classes).
 */
export function BrandStyleTag() {
  return (
    <style>{`
      @keyframes wedgeFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes wedgePulse { 0%,100% { opacity: 0.85; transform: scale(1); } 50% { opacity: 0.35; transform: scale(1.06); } }
      @keyframes wedgeRadarSweep { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes wedgeLiveDot { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
      .wedge-cta-emerald:hover:not(:disabled) { filter: brightness(1.07); transform: translateY(-1px); box-shadow: 0 10px 26px rgba(52,211,153,0.34) !important; }
      .wedge-cta-secondary:hover:not(:disabled) { background: rgba(52,211,153,0.13) !important; border-color: rgba(52,211,153,0.40) !important; transform: translateY(-1px); }
      .wedge-cta-ghost:hover:not(:disabled) { background: rgba(52,211,153,0.10) !important; border-color: rgba(52,211,153,0.34) !important; }
      .wedge-cta-link:hover { color: #34d399 !important; }
      @media (prefers-reduced-motion: reduce) { [data-wedge-animated] { animation: none !important; } }
    `}</style>
  );
}

/** Animated concentric-radar brand mark, matching the sign-in hero. */
export function BrandMark({ size = 76 }: { size?: number }) {
  const ring = Math.round(size * 0.84);
  return (
    <div
      data-wedge-animated
      style={{
        position: 'relative',
        width: size,
        height: size,
        margin: '0 auto 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: -8,
          borderRadius: '50%',
          border: '1.5px solid rgba(52,211,153,0.40)',
          animation: 'wedgePulse 2.4s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          overflow: 'hidden',
          pointerEvents: 'none',
          maskImage: 'radial-gradient(circle, #000 62%, transparent 64%)',
          WebkitMaskImage: 'radial-gradient(circle, #000 62%, transparent 64%)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: '-25%',
            background:
              'conic-gradient(from 0deg, rgba(52,211,153,0) 0deg, rgba(52,211,153,0) 250deg, rgba(52,211,153,0.10) 320deg, rgba(52,211,153,0.55) 358deg, rgba(52,211,153,0) 360deg)',
            animation: 'wedgeRadarSweep 3.6s linear infinite',
          }}
        />
      </div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background:
            'radial-gradient(circle at 38% 32%, rgba(52,211,153,0.28), rgba(10,26,16,0) 72%)',
          boxShadow:
            '0 0 0 1px rgba(52,211,153,0.28), inset 0 0 24px rgba(52,211,153,0.10), 0 0 56px rgba(52,211,153,0.22)',
        }}
      />
      <img
        src="/wedge/repvantage-radar.svg?v=20260712"
        alt="RepVantage"
        width={ring}
        height={ring}
        style={{ position: 'relative', zIndex: 1, filter: 'drop-shadow(0 2px 10px rgba(52,211,153,0.30))' }}
      />
    </div>
  );
}

/** REP (theme-light) + VANTAGE (amber) wordmark in Orbitron 800. */
export function BrandWordmark({ size = 20 }: { size?: number }) {
  return (
    <div
      style={{
        fontFamily: BRAND.fontDisplay,
        fontSize: size,
        fontWeight: 800,
        letterSpacing: 4,
        textTransform: 'uppercase',
        marginBottom: 7,
        textAlign: 'center',
      }}
    >
      <span style={{ color: '#F0F0F0' }}>REP</span>
      <span style={{ color: BRAND.amber }}>VANTAGE</span>
    </div>
  );
}

/** Share Tech Mono status pill with a live emerald dot. */
export function BrandChip({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 10.5,
        letterSpacing: 2.5,
        textTransform: 'uppercase',
        color: BRAND.accent,
        fontFamily: BRAND.fontMono,
        padding: '6px 12px',
        borderRadius: 999,
        background: 'rgba(52,211,153,0.06)',
        border: `1px solid ${BRAND.border}`,
        marginBottom: 14,
      }}
    >
      <span
        data-wedge-animated
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: BRAND.accent,
          boxShadow: '0 0 8px rgba(52,211,153,0.9)',
          animation: 'wedgeLiveDot 1.8s ease-in-out infinite',
        }}
      />
      {children}
    </div>
  );
}
